import { BHIParsedResult } from "@api/assessments/assessment-schema-types";
import type { AssessmentLayoutConfig, SelectOption } from "@components/AssessmentFormRenderer/types/layoutTypes";

// A user can only pick one of the four scored options below -- once picked,
// there's no way to clear the cell back to unscored without this explicit
// option, since the underlying Select has no clear-selection affordance.
const CLEAR_OPTION: SelectOption = { label: "— Not selected —", value: null };

// Per-subcategory dropdown text, transcribed verbatim from the BHI form grid
// -- must match BHI_DESCRIPTIONS in frontera's
// api/models/assessment_schemas/BHI/enrichment.py. "Behavior Medication" and
// "Injuries to Self or Others" have no printed text for score 3 on the form
// (the cell is blank), so that option is omitted rather than invented.
const BHI_SUBCATEGORY_SCORE_OPTIONS: Record<string, SelectOption[]> = {
  "Play / Leisure": [
    { label: "1 - No play skills", value: 1 },
    { label: "2 - Isolate play with objects & electronics", value: 2 },
    { label: "3 - Parallel play with others and engagement when alone", value: 3 },
    { label: "4 - Interactive and extended play with others and engagement when alone", value: 4 },
  ],
  "Stereotypic Behavior": [
    { label: "1 - Engages in intractable stereotypy & redirection occasions SPB", value: 1 },
    { label: "2 - Engages in stereotypy that interferes with learning and socializing", value: 2 },
    { label: "3 - Engages in stereotypy that sometimes requires redirection", value: 3 },
    { label: "4 - No stigmatizing or interfering stereotypy", value: 4 },
  ],
  "Functional Communication: Omnibus Mands": [
    { label: "1 - No independent omnibus mands", value: 1 },
    { label: "2 - Via simple response occurring in one context with only a few people", value: 2 },
    { label: "3 - Via a complex response occurring in one context & with various people", value: 3 },
    { label: "4 - Independent, omnibus mands across various people & contexts", value: 4 },
  ],
  "Functional Communication: Specific Mands": [
    { label: "1 - No independent specific mands", value: 1 },
    { label: "2 - Via simple responses occurring in one context with only a few people", value: 2 },
    { label: "3 - Via a complex response occurring in one context with various people", value: 3 },
    { label: "4 - Multiple specific mands across various people & contexts", value: 4 },
  ],
  "Delay Tolerance": [
    { label: "1 - SPB occurs when delays or denials are presented", value: 1 },
    { label: "2 - Mild PB is usually occasioned by denials", value: 2 },
    { label: "3 - No PB during delays or denials across a few contexts", value: 3 },
    { label: "4 - No PB during delays or denials across people & all contexts", value: 4 },
  ],
  "Contextually Appropriate Behavior (CAB)": [
    {
      label: "1 - Doesn't engage in any CAB when reinforcers are unavailable; instead SPB is occasioned by denials",
      value: 1,
    },
    {
      label: "2 - Reliably relinquishes, transitions, completes a few instructed responses in single activity/context",
      value: 2,
    },
    {
      label: "3 - Reliably completes a few instructed responses in multiple activities within single context",
      value: 3,
    },
    { label: "4 - Reliably engages in varied and extended CABs across various people & contexts", value: 4 },
  ],
  "Behavior Medication": [
    { label: "1 - Two or more medications being used to manage SPB", value: 1 },
    { label: "2 - One medication being used to manage SPB", value: 2 },
    { label: "4 - No medications being used to manage SPB", value: 4 },
  ],
  "Behavior Modification": [
    { label: "1 - Rich schedules of edible rewards", value: 1 },
    { label: "2 - Rich schedules of tokens exchangeable for edible & tangible rewards", value: 2 },
    { label: "3 - Lean schedule of tokens exchangeable for tangibles & activities", value: 3 },
    { label: "4 - No programmatic use of arbitrary rewards or punishers", value: 4 },
  ],
  "Behavior Mollification": [
    { label: "1 - Strong programmatic focus on mollification / avoidance tactics", value: 1 },
    { label: "2 - Programmatic use of mollification for safety while skills are developed", value: 2 },
    { label: "3 - Infrequent use of mollification for safety while skills are developed", value: 3 },
    { label: "4 - No programmatic use of mollification / avoidance tactics", value: 4 },
  ],
  "Restraint & Seclusion": [
    { label: "1 - Regular & noncontingent use of protective equipment, holds, or TO", value: 1 },
    { label: "2 - Regular & contingent use of protective equipment, holds, or TO", value: 2 },
    { label: "3 - Rare use of protective equipment, holds, or TO", value: 3 },
    { label: "4 - No use of protective equipment, holds, or TO", value: 4 },
  ],
  "Injuries to Self or Others": [
    { label: "1 - More than one staff / client injury in last 3 months", value: 1 },
    { label: "2 - One staff or client injury in last 3 months", value: 2 },
    { label: "4 - No staff or client injuries in last 3 months", value: 4 },
  ],
  "Location & Social Restriction": [
    { label: "1 - Almost always separate from peers with staff hovering at distance", value: 1 },
    { label: "2 - Usually in separate room with staff in close proximity", value: 2 },
    { label: "3 - Usually grouped with peers but restricted to certain contexts", value: 3 },
    { label: "4 - Grouped with peers and accesses most contexts with and without staff", value: 4 },
  ],
  Sleeping: [
    {
      label: "1 - Resists going to bed; long time to fall asleep, regularly wakes at night, gets little sleep",
      value: 1,
    },
    { label: "2 - 2 or 3 (of 4) sleep indicators showing risk", value: 2 },
    { label: "3 - 1 sleep indicator showing risk", value: 3 },
    { label: "4 - Goes to bed easily, falls asleep quickly; sleeps through night; gets enough sleep", value: 4 },
  ],
  Eating: [
    { label: "1 - Multi-faceted treatment; eats alone; is highly selective; weight is poor", value: 1 },
    { label: "2 - Some mealtime intervention; may eat with others; is selective", value: 2 },
    { label: "3 - No mealtime intervention; may eat with others; is selective", value: 3 },
    { label: "4 - No intervention; eats with others; is not overly selective, weight good", value: 4 },
  ],
  Exercising: [
    { label: "1 - No exercise experienced", value: 1 },
    { label: "2 - More than 10 min of moderate or vigorous exercise daily", value: 2 },
    { label: "3 - More than 30 min of vigorous exercise daily at least 3 times per week", value: 3 },
    { label: "4 - 45 min or more of vigorous activity at least 4 times per week", value: 4 },
  ],
};

const BHI_SUBCATEGORY_OPTIONS: Record<string, SelectOption[]> = Object.fromEntries(
  Object.entries(BHI_SUBCATEGORY_SCORE_OPTIONS).map(([name, options]) => [name, [CLEAR_OPTION, ...options]]),
);

// Initial Assessment Score comes from a raw handwritten digit (see extract.py
// -- it's read literally from the "INT. AST." cell, not tied to any of the
// four printed descriptions), so it gets a plain 1-4 selector rather than
// the rich per-subcategory text used for Assessment Today Score.
const BHI_PLAIN_SCORE_OPTIONS: SelectOption[] = [
  CLEAR_OPTION,
  { label: "1", value: 1 },
  { label: "2", value: 2 },
  { label: "3", value: 3 },
  { label: "4", value: 4 },
];

export const BHI_LAYOUT: AssessmentLayoutConfig = {
  parsedResultSchema: BHIParsedResult,
  sections: [
    {
      type: "text_field",
      key: "Child / Client",
      label: "Child / Client",
      editable: true,
    },
    {
      type: "text_field",
      key: "Scorer",
      label: "Scorer",
      editable: true,
    },
    {
      type: "text_field",
      key: "Initial Assessment Date",
      label: "Initial Assessment Date",
      editable: true,
      validation: { inputType: "date" },
    },
    {
      type: "text_field",
      key: "Today's Date",
      label: "Today's Date",
      editable: true,
      validation: { inputType: "date" },
    },
    {
      type: "score_table",
      label: "Subcategory Scores",
      source: "Subcategories",
      columns: [
        { key: "Name", label: "Sub-Category", editable: false, width: "w-80" },
        {
          key: "Initial Assessment Score",
          label: "Initial Ast.",
          editable: true,
          width: "w-24",
          options: BHI_PLAIN_SCORE_OPTIONS,
        },
        {
          key: "Assessment Today Score",
          label: "Ast. Today",
          editable: true,
          width: "w-96",
          // Falls back to the plain 1-4 selector (rather than an
          // uncontrolled free-text input) if an extracted subcategory Name
          // doesn't exactly match an optionsMap key.
          options: BHI_PLAIN_SCORE_OPTIONS,
          optionsByRow: { keyColumn: "Name", optionsMap: BHI_SUBCATEGORY_OPTIONS },
        },
      ],
    },
  ],
};
